import {
  PaginatedTable,
  IPagedResultsV1,
  Column,
  TableCellExtras,
  Icon,
  Tooltip,
  createTableCell,
  createTableColumn,
} from '@gcl/react-core';
import * as React from 'react';
import { TableContentLoader } from '@common/TableContentLoader';
import {
  IProjectTransmittalHistory,
  IProjectTransmittalHistoryItem,
  IProjectTransmittalHistoryItemRevision,
} from '@transmittal/history/transmittalHistory.models';
import { TransmittalHistoryTableLayout } from '../../TransmittalHistoryTableLayout';
import { LatestTransmittalRevisionList } from './LatestTransmittalRevisionList';
import './TransmittalItemTable.scss';
import { createTransmittalItemDownloadColumn } from './transmittalItemTableColumn.utils';

export interface ITransmittalItemTableProps {
  transmittalHistoriesPagedResult?: IPagedResultsV1<IProjectTransmittalHistory> | undefined;
  transmittalHistoryItems?: IProjectTransmittalHistoryItem[] | undefined;
  transmittalItemsLatestRevision?: IProjectTransmittalHistoryItemRevision[];
  isLoading: boolean;
  hoverTransmittalId: number | undefined;
  setHoverTransmittalId: React.Dispatch<React.SetStateAction<number | undefined>>;
}

export const TransmittalItemTable: React.FunctionComponent<ITransmittalItemTableProps> = ({
  transmittalHistoriesPagedResult,
  transmittalHistoryItems = [],
  transmittalItemsLatestRevision = [],
  isLoading = true,
  hoverTransmittalId,
  setHoverTransmittalId,
}) => {
  const defaultColumns: Column[] = React.useMemo(() => {
    return [
      createTableColumn({
        disableSortBy: true,
        accessor: 'id',
        Header: () => {
          return (
            <div className="gcl-gems-transmittal-history-page-item-cols">
              <div>Doc. No.</div>
              <div>Description</div>
            </div>
          );
        },
        disableResizing: true,
        Cell: createTableCell<number, IProjectTransmittalHistoryItem>(
          (value: number, item: IProjectTransmittalHistoryItem, index: number, cellExtras: { [key: string]: any }) => {
            return (
              <div className="gcl-gems-transmittal-history-page-item-cols">
                <Tooltip overlay={item.docNo} id="gcl-gems-transmttal-history-page-doc-no">
                  <div className="gcl-txt-of-l1">{item.docNo}</div>
                </Tooltip>
                <Tooltip overlay={item.description} id="gcl-gems-transmttal-history-page-desc">
                  <div className="gcl-txt-of-l1">{item.description}</div>
                </Tooltip>
              </div>
            );
          },
        ),
      } as Column),
    ];
  }, []);
  const [columns, setColumns] = React.useState<Column[]>([...defaultColumns]);

  React.useEffect(() => {
    if (isLoading) return;

    if (
      !transmittalHistoriesPagedResult ||
      !transmittalHistoriesPagedResult.items ||
      transmittalHistoriesPagedResult.items.length <= 0
    ) {
      setColumns([...defaultColumns]);
      return;
    }

    const tempColumns: Column[] = (transmittalHistoriesPagedResult?.items || []).map((transmittalHistory) => {
      return {
        ...createTransmittalItemDownloadColumn(
          transmittalHistory.id.toString(),
          '',
          (value, item: IProjectTransmittalHistoryItem) => {
            if (!item.id || item.id <= 0) return {};

            if (item.lastTransmittalId && item.lastTransmittalId === transmittalHistory.id)
              return {
                defaultElement: (
                  <Tooltip overlay="Removed From Use" id="gcl-gems-transmttal-history-page-removed-from-use">
                    <Icon iconName="LockFill" className="gcl-gems-transmittal-history-page-removed-from-use" />
                  </Tooltip>
                ),
              };

            const transmittalRevision = transmittalHistory.revisions.find((r) => r.itemId === item.id);
            if (!transmittalRevision) return {};

            return {
              projectFileId: transmittalRevision.projectFile.id,
              projectId: transmittalRevision.projectId,
              displayText: transmittalRevision.revisionNo,
            };
          },
          true,
        ),
        id: transmittalHistory.id.toString(),
      } as Column;
    });
    setColumns([...defaultColumns, ...tempColumns]);
  }, [JSON.stringify(transmittalHistoriesPagedResult)]);

  if (isLoading) return <TableContentLoader isLoading={isLoading} />;

  return (
    <TableContentLoader isLoading={isLoading}>
      <TransmittalHistoryTableLayout
        mainTable={
          <div
            className="gcl-gems-transmittal-item-table"
            onMouseLeave={() => {
              setHoverTransmittalId(undefined);
            }}
          >
            <PaginatedTable
              columns={columns}
              items={transmittalHistoryItems}
              hoverHighlight
              disablePagination
              allowExpandRow={false}
              allowSelectRow={false}
              responsive={false}
              classNames={{
                dataCell: (item: object | undefined, column: Column, cellExtras: TableCellExtras | undefined) => {
                  if (
                    column.id &&
                    Number(column.id?.trim()) &&
                    Number(column.id?.trim()) === cellExtras?.hoverTransmittalId
                  )
                    return 'gcl-gems-transmittal-history-page-table-cell-highlight';
                  return '';
                },
              }}
              cellExtras={{ hoverTransmittalId, setHoverTransmittalId }}
              eventProps={{
                dataCell: {
                  onMouseEnter: (item: object | undefined, column: Column, cellExtras: TableCellExtras | undefined) => {
                    if (column.id && Number(column.id?.trim()))
                      cellExtras?.setHoverTransmittalId(Number(column.id?.trim()));
                    else cellExtras?.setHoverTransmittalId(undefined);
                  },
                },
              }}
            />
          </div>
        }
        pkgTable={
          <LatestTransmittalRevisionList
            transmittalHistoryItems={transmittalHistoryItems}
            transmittalItemsLatestRevision={transmittalItemsLatestRevision}
          />
        }
      />
    </TableContentLoader>
  );
};
